import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Users, Calendar, ChevronLeft, Trash2, Edit3, Share2, Copy } from 'lucide-react';
import Navbar from '../components/Navbar';
import Avatar from '../components/Avatar';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/auth/me');
      const userGroups = data.user?.groups || [];
      if (!userGroups.length) { setLoading(false); return; }

      const results = await Promise.allSettled(
        userGroups.map(g => {
          const id = typeof g === 'string' ? g : g._id;
          return API.get(`/groups/${id}`);
        })
      );
      const loaded = results
        .filter(r => r.status === 'fulfilled')
        .map(r => r.value.data.group);
      setGroups(loaded);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const adminGroups = groups.filter(g => g.admin?._id === user?._id || g.admin === user?._id);
  const memberGroups = groups.filter(g => g.admin?._id !== user?._id && g.admin !== user?._id);

  return (
    <div className="page-wrapper">
      <Navbar />
      <div className="container" style={{ padding: '32px 20px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '4px' }}>
              أهلاً، {user?.name?.split(' ')[0]} 👋
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              إجمالي مجموعاتك: {groups.length}
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={18} /> إنشاء مجموعة جديدة
          </button>
        </div>

        {loading ? (
          <div className="loading-screen"><div className="spinner" /><p>جاري تحميل المجموعات...</p></div>
        ) : groups.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🎉</div>
            <h3>مفيش مجموعات لحد دلوقتي</h3>
            <p style={{ marginBottom: '20px' }}>أنشئ أول مجموعة وابدأ التنظيم!</p>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              <Plus size={16} /> إنشاء مجموعة
            </button>
          </div>
        ) : (
          <>
            {adminGroups.length > 0 && (
              <Section title="مجموعاتي كأدمن 👑" groups={adminGroups} isAdmin user={user} navigate={navigate} onRefresh={() => { refreshUser(); loadGroups(); }} />
            )}
            {memberGroups.length > 0 && (
              <Section title="مجموعات انضممت إليها" groups={memberGroups} user={user} navigate={navigate} onRefresh={loadGroups} />
            )}
          </>
        )}
      </div>

      {showCreate && <CreateGroupModal onClose={() => setShowCreate(false)} onSuccess={() => { setShowCreate(false); loadGroups(); }} />}
    </div>
  );
};

const Section = ({ title, groups, isAdmin, user, navigate, onRefresh }) => (
  <div style={{ marginBottom: '40px' }}>
    <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
      {title}
      <span className="badge badge-secondary">{groups.length}</span>
    </h2>
    <div className="grid-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
      {groups.map(g => (
        <GroupCard key={g._id} group={g} isAdmin={isAdmin} navigate={navigate} onRefresh={onRefresh} />
      ))}
    </div>
  </div>
);

const GroupCard = ({ group, isAdmin, navigate, onRefresh }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`هل أنت متأكد من حذف "${group.name}"؟`)) return;
    setDeleting(true);
    try {
      await API.delete(`/groups/${group._id}`);
      toast.success('تم حذف المجموعة');
      onRefresh();
    } catch {
      toast.error('فشل الحذف');
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyLink = async (e) => {
    e.stopPropagation();
    const link = `${window.location.origin}/join/${group.inviteToken}`;
    await navigator.clipboard.writeText(link);
    toast.success('تم نسخ رابط الدعوة!');
  };

  const membersCount = group.members?.length || 0;
  const percentage = Math.round((membersCount / group.maxMembers) * 100);

  return (
    <div
      className="card card-hover"
      style={{ cursor: 'pointer', padding: 0, overflow: 'hidden' }}
      onClick={() => navigate(`/group/${group._id}`)}
    >
      {/* Group Image */}
      <div style={{
        height: '120px',
        background: group.image
          ? `url(${group.image}) center/cover`
          : 'var(--gradient-primary)',
        position: 'relative',
      }}>
        {isAdmin && (
          <div style={{ position: 'absolute', top: '8px', left: '8px', display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
            <button className="btn btn-sm" style={{ background: 'rgba(0,0,0,0.6)', color: 'white', padding: '4px 8px', backdropFilter: 'blur(4px)' }}
              onClick={handleCopyLink}>
              <Copy size={12} />
            </button>
            <button className="btn btn-sm btn-danger" style={{ padding: '4px 8px' }}
              onClick={handleDelete} disabled={deleting}>
              <Trash2 size={12} />
            </button>
          </div>
        )}
        {!group.image && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>
            🎉
          </div>
        )}
      </div>

      <div style={{ padding: '16px' }}>
        <h3 style={{ fontWeight: 700, marginBottom: '4px', fontSize: '1rem' }}>{group.name}</h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '12px' }}>
          <Users size={13} />
          <span>{membersCount} / {group.maxMembers} عضو</span>
        </div>

        {/* Progress */}
        <div className="progress-bar" style={{ marginBottom: '12px' }}>
          <div className="progress-fill" style={{ width: `${percentage}%` }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex' }}>
            {(group.members || []).slice(0, 4).map((m, i) => (
              <Avatar
                key={i} src={m.avatar} name={m.name} size="sm"
                style={{ marginLeft: i > 0 ? '-8px' : 0, border: '2px solid var(--bg-card)', zIndex: 4 - i }}
              />
            ))}
            {membersCount > 4 && (
              <div className="avatar avatar-sm" style={{ marginLeft: '-8px', zIndex: 0, fontSize: '0.7rem' }}>
                +{membersCount - 4}
              </div>
            )}
          </div>
          <ChevronLeft size={16} style={{ color: 'var(--text-muted)' }} />
        </div>
      </div>
    </div>
  );
};

const CreateGroupModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ name: '', maxMembers: '' });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.maxMembers) { toast.error('اسم المجموعة وعدد الأعضاء مطلوبان'); return; }
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('maxMembers', form.maxMembers);
    if (image) fd.append('image', image);

    setLoading(true);
    try {
      await API.post('/groups', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('تم إنشاء المجموعة 🎉');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في الإنشاء');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">إنشاء مجموعة جديدة 🎉</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Image */}
          <div className="form-group">
            <label className="form-label">صورة المجموعة (اختياري)</label>
            <label className="file-upload-area" style={{ display: 'block', cursor: 'pointer' }}>
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files[0]; setImage(f); setPreview(URL.createObjectURL(f)); }} />
              {preview ? (
                <img src={preview} alt="" style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: 'var(--radius-md)' }} />
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <Share2 size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                  <p style={{ fontSize: '0.85rem' }}>اضغط لاختيار صورة</p>
                </div>
              )}
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">اسم المجموعة *</label>
            <input type="text" className="form-input" placeholder="مثال: شاليه الصيف 🌊"
              value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>

          <div className="form-group">
            <label className="form-label">الحد الأقصى للأعضاء *</label>
            <input type="number" className="form-input" placeholder="مثال: 20" min="2" max="500"
              value={form.maxMembers} onChange={e => setForm({ ...form, maxMembers: e.target.value })} />
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>إلغاء</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 2 }} disabled={loading}>
              {loading ? <div className="spinner spinner-sm" /> : <Plus size={16} />}
              {loading ? 'جاري الإنشاء...' : 'إنشاء المجموعة'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Dashboard;